import { types, getEnv, flow, SnapshotOut, Instance } from 'mobx-state-tree';
import { observable } from 'mobx';
import { ICommonAppEnvWithSBBaseApi, IOdd, IOddsExtData } from '../index';
import { translation } from 'easit--localization';
import { TicketSelection, ITicketSelection, RateChange } from './ticketSelection';
import {
    StakeType, OddsChangeType, ITicketResponse, TicketType, ITicketConfirmResponse, ResultType,
    OfferType, ITicketPrepareResponse, TicketErrorType, ITicketOneTapBetStartResponse,
    AMLWidgetType
} from '../../api/proto/apiContract';
import {
    ITicketSelectionRequest, ITicketBetRequest, getSucceededPromiseAsync, ISportsBettingBaseApi, getFailedPromiseAsync,
    errorIsCancel, errorIsSessionExpired, ITicketValidateParams, IAmlOptionsRequest
} from '../../api/apiBase';
import { TicketBet, ITicketBet } from './ticketBet';
import { TicketStatusType, RateChangeAcceptType } from '../../coupon/enums';
import { ITicketStorageData, ITicketStorageSelection, ITicketStorageBet } from './ticketStorage';
import { parseTipIdent } from '../event';
import { TelemetryServiceDataType } from '../../services/telemetryService';
import { IGroupedApiCtx, protoUtils } from '../../api/apiCommon';
import { currency, pointsCurrency, translationForMts } from '../../../../localization/localization';
import { ICommonTemplateSettings } from '../index';
import { gAService } from '../../services/windowWithGa';
import { NumberFromInput } from './numberFromInput';
import { ModifyOddsCtxWrapper } from './modifyOddsCtxWrapper';

export const TicketStatusTypeForStore = types.enumeration('TicketStatusTypeForStore', [TicketStatusType.New, TicketStatusType.Pay, TicketStatusType.Accepted, TicketStatusType.Rejected,
TicketStatusType.Saving, TicketStatusType.Hint, TicketStatusType.Saved]);

export enum AMLWidgetTypeUI {
    Default = 0,
    Label = 1,
    Select = 2
}

const minimumSelectionsForSystem = 2;

export enum TicketMaxStakeVisibility {
    None = 0,
    NewLine = 1,
    InStakeLabel = 2
}

export interface IValidateTicketAsyncParams {
    showUpdateError: boolean;
    sourceForLog: string;
    skipIfAlreadyInProgress?: boolean;
}

export const AccountTypeKey = types.model({
    id: types.string,
    freeTicketId: types.maybeNull(types.number)
});

export const AvailableAccountType = types.model({
    key: AccountTypeKey,
    name: types.string,
    isMoney: types.boolean,
    hint: types.maybeNull(types.string),
    stakeValue: types.maybeNull(types.number),
    canBeUsedByPlayer: types.boolean
}).actions(self => {
    return {
        //Compares self.key with key passed in method parameter
        areKeysEqual: (keyToCompare: Instance<typeof AccountTypeKey>) => {
            if ((self.key && !keyToCompare) || (!self.key && keyToCompare)) {
                return false;
            }
            return self.key.id === keyToCompare.id && self.key.freeTicketId === keyToCompare.freeTicketId;
        }
    }
});

export const MoneyWithCurrency = types.model({
    amount: types.number,
    currency: types.string
});

export const AMLSubOption1OfNItem = types.model({
    id: types.number,
    label: types.string,
});

export const AMLSubOption1OfN = types.model({
    items: types.array(AMLSubOption1OfNItem),
    selectedId: types.maybeNull(types.number),
}).actions(self => {
    return {
        setSelected: (id: number | null) => { self.selectedId = id; }
    }
});


export const AMLSubOptionText = types.model({
    label: types.string,
    content: types.maybeNull(types.string),
}).actions(self => {

    const contentChanged = (newValue: string) => {
        self.content = newValue;
    }

    return {
        contentChanged
    }
});

export const AMLOptionItem = types.model({
    id: types.number,
    label: types.string,
    widgetType: types.optional(types.number, AMLWidgetTypeUI.Default), // enum see AMLTypeOnUI
    textAtEnd: types.maybeNull(types.string),
    isSelected: types.maybeNull(types.boolean),
    subItem1OfN: types.maybeNull(AMLSubOption1OfN),
    subItemText: types.maybeNull(AMLSubOptionText),
    disabledIfDef: types.optional(types.map(types.array(types.number)), {}),
    isDisabled: types.boolean,
}).actions(self => {

    const afterSelected = () => {
        // if (self.subItem1OfN) {
        //     self.subItem1OfN.setSelected(self.subItem1OfN.items[0].id); //ad hoc as requested for the income from work, should be on a "config" value
        // }
    }

    return {

        setDisableIfDefFromApi: (source: string[]) => {
            source.forEach(item => {
                let vals = item.split(".");
                let key = vals[0].toString();
                let destArr: number[] = self.disabledIfDef.get(key);
                if (!destArr) {
                    destArr = [];
                    self.disabledIfDef.set(key, destArr);
                    destArr = self.disabledIfDef.get(key);
                }
                destArr.push(Number(vals[1]));
            })
        },

        /**
         * Toggle select. ! Call updateDisabled(); for parent!
         */
        toggleSelect: () => {
            self.isSelected = !self.isSelected;
            if (self.isSelected) {
                afterSelected();
            } else {
                if (self.subItem1OfN) {
                    self.subItem1OfN.setSelected(null);
                }
            }
        },
        /**
         * Set as selected. ! Call updateDisabled(); for parent!
         */
        setSelected: (subItemId?: number) => {

            if (subItemId !== undefined) {
                self.subItem1OfN.setSelected(subItemId);
            }
            if (self.widgetType === AMLWidgetTypeUI.Select && subItemId === 0) {
                self.isSelected = false;
            } else {
                self.isSelected = true;
                afterSelected();
            }
        }
    }
});


export type IMoneyWithCurrency = Instance<typeof MoneyWithCurrency>;
export type IAccountTypeKey = Instance<typeof AccountTypeKey>;
//export type IAvailableAccountType = Instance<typeof AvailableAccountType>;

export const AMLOptionsStore = types.model({
    items: types.optional(types.array(AMLOptionItem), []),
}).actions((self) => {
    const toggleAMLOption = (id: number): void => {
        const option = self.items.find(aml => aml.id === id);
        if (option) {
            option.toggleSelect();
        }
        updateDisabled();
    }

    const updateDisabled = (): void => {
        self.items.forEach(item => {
            /*if (!item.disabledIfDef.) {
                return;
            }*/
            let newIsDisabled = false;
            //console.info("updateDisabled", item.id);
            item.disabledIfDef.forEach((disabledIfDefRow, key) => {
                //console.info("updateDisabled in disabledIfDef", key);
                let foundDest = self.items.find(destItem => destItem.id.toString() == key)
                if (!foundDest.isSelected) {
                    newIsDisabled = true;
                } else if (foundDest.subItem1OfN) {
                    newIsDisabled = disabledIfDefRow.some(disabledIfId => foundDest.subItem1OfN.selectedId == disabledIfId);
                }
                if (newIsDisabled) {
                    return;
                }
            });
            if (newIsDisabled) {
                if (item.isSelected) {
                    item.toggleSelect();
                }
            }
            item.isDisabled = newIsDisabled;
        });
    }

    return {
        toggleAMLOption,
        updateDisabled
    }
});

export const MtsTicket = types
    .model({
        offerType: types.number, //OfferType
        id: types.maybeNull(types.string), //may be long and protobuf works with Long
        stake: types.maybeNull(NumberFromInput),  //in self.currency
        stakeMoneyForInfo: types.maybeNull(types.number), // stake in money for information if primary stake/currency is in points
        ticketType: types.optional(types.number, TicketType.Simple), // TicketType
        oddsChangeType: types.optional(types.number, OddsChangeType.None), // OddsChangeType, see also view rateChangeAcceptType and reverse convertRateChangeAcceptType2OddsChangeType
        selections: types.optional(types.array(TicketSelection), []),
        bets: types.maybeNull(types.array(TicketBet)),
        availableAccountTypes: types.optional(types.array(AvailableAccountType), []),
        selectedAccountType: types.maybeNull(AccountTypeKey), //when changing, use also fixStakeAfterChangeOfAccountType!. Empty string is None, relevant for !isSystem.
        stakeType: types.optional(types.number, StakeType.All), // StakeType
        stakeWhole: types.maybeNull(types.number), //in self.currency
        rate: types.maybeNull(types.number),
        potentialWin: types.maybeNull(types.number), // in common currency (easit--localization)
        productFee: types.maybeNull(MoneyWithCurrency),
        oddsRateAdvantage: types.maybeNull(types.number),
        rateAdvantageFactor: types.maybeNull(types.number),
        ticketState: types.optional(TicketStatusTypeForStore, TicketStatusType.New),
        isValidateTicketInProgress: types.maybeNull(types.boolean),
        isAcceptChangedRatesAndEvalInProgress: types.maybeNull(types.boolean),
        isTicketCopyInProgress: types.maybeNull(types.boolean),
        auxIsSystem: types.maybeNull(types.boolean), //for UI
        auxBetStorageData: types.maybeNull(types.string),
        waitingInPayProgress: types.maybeNull(types.number),
        maxStake: types.maybeNull(types.number), // in self.currency
        index: types.maybeNull(types.number), // ?
        errors: types.maybeNull(types.array(types.string)), //use setErrors to set
        errorsAboveSubmitButtons: types.maybeNull(types.array(types.string)), //use setErrors to set
        hint: types.maybeNull(types.number), // for api, in money!
        hintForUser: types.maybeNull(types.number), //to be shown to user (rounded), in self.currency
        isAcceptingHintInProgress: types.maybeNull(types.boolean),
        handlingFeeValue: types.maybeNull(types.number),
        handlingFeePointsValue: types.maybeNull(types.number),

        savedTicketNumber: types.maybeNull(types.string),
        savedTicketExpiration: types.maybeNull(types.Date),

        amlOptions: types.maybeNull(AMLOptionsStore),

        isOTBSettingSet: types.optional(types.boolean, false)
    })
    .views(self => {

        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

        const canValidateTicket = (): boolean => {
            return (
                self.ticketState === TicketStatusType.New
            );
        };

        const isSystem = (): boolean => {
            return self.ticketType == TicketType.System;
        };

        const canModifyOdds = (): boolean => {
            return (
                self.ticketState === TicketStatusType.New
            );
        };

        const getIsStakeAsMoney = (): boolean => {
            if (self.availableAccountTypes && self.selectedAccountType) {
                let found = self.availableAccountTypes.find(item => item.areKeysEqual(self.selectedAccountType));
                if (found) {
                    return found.isMoney;
                }
            }
            return true;
        }

        return {
            get selectionsCount() {
                return self.selections.length;
            },

            get tipIdents(): string[] {
                return self.selections.map(odd => odd.indent);
            },

            get isFinalTicketState(): boolean {
                return (
                    self.ticketState === TicketStatusType.Accepted
                    || self.ticketState === TicketStatusType.Rejected
                    || self.ticketState === TicketStatusType.Saved
                    //|| (self.betType == BetType.Prematch && self.ticketState === TicketStatusType.WaitingForAccept) todon
                );
            },

            get canValidateTicket(): boolean {
                return canValidateTicket();
            },

            get isSystem(): boolean {
                return isSystem();
            },

            get canSetSystemTicketType(): boolean {
                return !isSystem() && self.selections.length /* tipsCount */ >= minimumSelectionsForSystem && self.ticketState === TicketStatusType.New && !self.isValidateTicketInProgress;
            },

            get isValidStakeInputs(): boolean {
                //it may be verified using value e.g. 15.5.5 (copy from clipboard)
                if (isSystem()) {
                    if (self.stakeType == StakeType.Total || self.stakeType == StakeType.Unit) {
                        return self.bets.every(bet => !bet.isChecked || bet.stake.isValid);
                    }
                    return self.stake.isValid;
                }
                return self.stake.isValid;
            },

            get isSummaryValid(): boolean {
                return !(self.errors && self.errors.length > 0);
            },

            get isEnabledUI(): boolean {
                return self.ticketState === TicketStatusType.New && (!isSystem() || !self.isValidateTicketInProgress); // see also isSystemCouponUpdating
            },

            get isFreeTicketAccountTypeSelected(): boolean {
                return self.selectedAccountType ? self.selectedAccountType.freeTicketId !== null : false;
            },

            get isSystemTicketUpdating() {
                return isSystem() && (self.isValidateTicketInProgress);  //see also isEnabledUI
            },

            get canModifyOdds() {
                return canModifyOdds();
            },

            get stakesFix(): any[] {
                return env.settings.stakeButtons;
            },

            get areRatesUpToDate(): boolean {
                return !self.selections.find(odd => odd.rate != odd.rateAccepted);
            },

            get isStakeAsMoney(): boolean {
                return getIsStakeAsMoney();
            },

            get useDecimalPlacesForStake(): boolean {
                if (env.settingsFromApi.ticketStakeDecimalPlaces == 0) {
                    return false;
                }
                return getIsStakeAsMoney();
            },

            get currency(): string {
                if (getIsStakeAsMoney()) {
                    return env.settings.currency;
                } else {
                    return env.settings.pointsCurrency;
                }
            },

            get canPayOrSave(): boolean {
                return canModifyOdds();
            },

            get isFinalticketState(): boolean {
                return (
                    self.ticketState === TicketStatusType.Accepted ||
                    self.ticketState === TicketStatusType.Rejected
                );
            },

            get isLoggedUser(): boolean {
                return env.loggedUserId > 0;
            },

            get liveCouponPayWaitStepMs(): number { // not to double interval when user resend it
                return 200;
            },

            get rateChangeAcceptType(): RateChangeAcceptType {
                switch (self.oddsChangeType) {
                    case OddsChangeType.None: return RateChangeAcceptType.None;
                    case OddsChangeType.Higher: return RateChangeAcceptType.RateIncrease;
                    case OddsChangeType.Any: return RateChangeAcceptType.AnyRate;
                }
                throw new Error("Unexpected value " + self.oddsChangeType);
            },

            get template(): ICommonTemplateSettings {
                return env.settings.template;
            },

            get canPrepareTicket(): boolean {
                return self.offerType !== OfferType.Live && self.ticketType === TicketType.Simple && env.loggedUserId === 0 && env.settingsFromApi.canPrepareTicket;
            },

            get visualDelay(): number {
                return env.settingsFromApi.timeForCoupon / 1000;
            },

            get canShowAliasNumber(): boolean {
                return env.settings.canShowAliasNumberInTicket;
            },

            get canShowProductsInTicket(): boolean {
                return env.settings.canShowProductsInTicket;
            },
            get ticketMaxStakeVisibility(): TicketMaxStakeVisibility {
                return env.settings.ticketMaxStakeVisibility;
            },
            get getFreeTicketBonusUrl(): string {
                return env.settings.freeTicketBonusUrl;
            }
        }
    })
    .actions(self => {
        const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);
        let validateTicketCtxWrapper: ModifyOddsCtxWrapper | null = null;  // now Api needs to have just one call to coupon requests (modifyOdds, couponPay etc). It means now I need to wait until previous request finishes.
        const canAutoUpdateRates = true;

        /**
         * @throws Error in case of incompatibility! Store may be in incosinstent state, called have to solve it.
         */

        const isMtsTicketEmpty = () => {
            return !self.isFinalTicketState && self.selectionsCount === 0;
        }

        const setDataFromStorage = (data: ITicketStorageData): void => {
            self.id = null;
            self.ticketType = data.ticketType;
            self.stakeType = data.stakeType;
            self.oddsChangeType = data.oddsChangeType;
            self.potentialWin = data.prizePotential;
            self.rate = data.totalRate;
            self.productFee = data.productFee;
            self.oddsRateAdvantage = data.oddsRateAdvantage;
            self.rateAdvantageFactor = data.rateAdvantageFactor;
            self.handlingFeeValue = data.handlingFeeValue;
            self.handlingFeePointsValue = data.handlingFeePointsValue;

            let selections: Instance<typeof TicketSelection>[] = [];
            if (data.selections) {
                data.selections.forEach(selection => {
                    selections.push(TicketSelection.create(<SnapshotOut<typeof TicketSelection>>{
                        id: selection.id,
                        fixtureId: selection.fixtureId,
                        gameId: selection.gameId,
                        rate: selection.rate,
                        rateAccepted: selection.rate,
                        marketName: selection.marketName,
                        tipName: selection.tipName,
                        isBanker: selection.isBanker,
                        sportName: selection.sportName,
                        fixtureName: selection.fixtureName,
                        fixtureDate: selection.fixtureDate?.getTime(),
                        errors: [],
                        indent: selection.indent,
                        number: selection.number,
                        offerType: selection.offerType,

                        rateChange: null
                    }));
                });
            }
            self.selections = observable(selections);
            evaluateAndUpdateOfferType();
            if (self.ticketType == TicketType.Simple) {
                setStake(data.bets[0].stake);
            } else {
                let possibleBets = evaluatePossibleBets();
                let bets: Instance<typeof TicketBet>[] = [];

                possibleBets.forEach(bet => {
                    let srcBet = data.bets.find(d => d.id == bet.id);
                    bets.push(TicketBet.create(<SnapshotOut<typeof TicketBet>>{
                        id: bet.id,
                        title: bet.title,
                        isChecked: !!srcBet,
                        stake: srcBet ? NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{
                            isValid: true,
                            value: srcBet.stake
                        }) : null,
                        combinationCount: bet.combinationCount,
                        unitStake: srcBet ? srcBet.unitStake : null
                    }));
                });
                self.bets = observable(bets);
                setStake(data.stake);
                self.stakeWhole = data.stake;
            }

            self.availableAccountTypes = observable([]);
            if (data.availableAccountTypes) {
                data.availableAccountTypes.forEach(accountType => {
                    self.availableAccountTypes.push(
                        AvailableAccountType.create(<SnapshotOut<typeof AvailableAccountType>>{
                            key: AccountTypeKey.create(<SnapshotOut<typeof AccountTypeKey>>{ ...accountType.key })
                            , name: accountType.name, isMoney: accountType.isMoney, hint: accountType.hint, stakeValue: accountType.stakeValue, canBeUsedByPlayer: accountType.canBeUsedByPlayer
                        })
                    )
                })
            }
            self.selectedAccountType = AccountTypeKey.create(<SnapshotOut<typeof AccountTypeKey>>{ ...data.selectedAcountType });

            self.stakeMoneyForInfo = data.stakeMoneyForInfo;
        };

        const clearCouponAsync = (preventModifyOdds: boolean = false): Promise<void> => {
            self.ticketState = TicketStatusType.New;
            if (!self.isOTBSettingSet) {
                setStake(env.settingsFromApi.stakeMin);
            }
            self.stakeMoneyForInfo = null;
            self.ticketType = TicketType.Simple;
            self.selections = observable([]);
            self.offerType = OfferType.Prematch; // same in evaluateAndUpdateOfferType();
            self.rate = null;
            self.potentialWin = null;
            self.productFee = null;
            self.oddsRateAdvantage = null;
            self.rateAdvantageFactor = null;
            self.handlingFeeValue = null;
            self.handlingFeePointsValue = null;
            //! do not reset self.oddsChangeType, it should remain from last ticket
            setErrors([]);
            self.bets = observable([]);

            self.maxStake = 0;
            self.selectedAccountType = null;
            self.availableAccountTypes = observable([]);

            self.amlOptions = AMLOptionsStore.create();

            if (preventModifyOdds === false) {
                return validateTicketAsync({ showUpdateError: false, sourceForLog: 'clearCouponAsync' });
            }
            return getSucceededPromiseAsync();
        };

        const setStake = (value: number): void => {
            setStakeOrValidity(true, value);
        };

        const processBetStakes = (): void => {
            if (self.isSystem && self.stakeType === StakeType.All) {
                let combinationsCnt = 0;
                self.bets.filter(bet => bet.isChecked).forEach(bet => { combinationsCnt += bet.combinationCount });

                if (combinationsCnt > 0) {
                    let stakePerCombi = self.stake.value / combinationsCnt;
                    if (__IsMTSTipos__ && stakePerCombi < env.settingsFromApi.combiStakeMin) { //probably for Synot it becomes very rarely, but we agreed not to do for Synot
                        setStake(roundStakeInCombi(env.settingsFromApi.combiStakeMin * combinationsCnt));
                        stakePerCombi = env.settingsFromApi.combiStakeMin;
                    }
                    self.bets.forEach(bet => { if (bet.isChecked) { bet.stake.value = stakePerCombi * bet.combinationCount } });
                }
            }
        }

        const setStakeOrValidity = (isValid: boolean, value?: number): void => {
            if (self.stake) {
                self.stake.setValidityAndValue(isValid, value);
            } else {
                self.stake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{
                    value: value,
                    isValid: isValid
                });
            }

        };

        const minimizeStake = () => {
            setStake(env.settingsFromApi.stakeMin);
        };

        const stakeChangedAsync: () => Promise<void> = () => {
            if (self.canModifyOdds) {
                fixStakeAfterChangeOfPoints();
                processBetStakes();
                return validateTicketAsync({ showUpdateError: true, sourceForLog: 'stakeChangedAsync' });
            }
            return getSucceededPromiseAsync();
        }

        const evaluateOfferType = (): OfferType => {
            return self.selections.some(selection => selection.offerType === OfferType.Live) ? OfferType.Live : OfferType.Prematch;
        }

        const evaluateAndUpdateOfferType = (): void => {
            self.offerType = evaluateOfferType();
        }

        /**
         * Adds a row into coupon in state allowing canModifyOdds. Otherwise does nothing.
         */
        const addTipAsync = (ident: string, odd: IOdd, eventGameData: IOddsExtData, preventModifyOdds: boolean = false, canAnimate: boolean = false): Promise<void> => {
            if (self.ticketState === TicketStatusType.Accepted) {
                self.ticketState = TicketStatusType.New;
            }

            if (self.isOTBSettingSet && self.selections.length > 0) {
                return getSucceededPromiseAsync();
            }

            if (!self.canValidateTicket) {
                return getFailedPromiseAsync(new Error("Cannot change coupon"));
            }

            const parsedIdent = parseTipIdent(ident);

            const selectionId = parsedIdent.tipID;
            const selection = self.selections.find(tip => tip.fixtureId === parsedIdent.eventID && tip.gameId == parsedIdent.gameID); //TODON detect same game?
            if (selection) {
                if (selection.id === selectionId) { //shouldn't come here
                    env.loggingService.warning("Weird - adding the same selection " + parsedIdent.tipID);
                } else {
                    selection.id = selectionId;
                    selection.tipName = odd.nameForTicket ? odd.nameForTicket : odd.name;
                    selection.rate = parsedIdent.rate;
                    selection.rateAccepted = parsedIdent.rate;
                    selection.indent = ident;
                    selection.number = eventGameData.number; //should be same but for sure
                    selection.errors = observable([]);
                    selection.offerType = eventGameData.offerType;
                    selection.canAnimate = canAnimate
                }
            } else {
                self.selections.push(TicketSelection.create(<SnapshotOut<typeof TicketSelection>>{
                    id: selectionId,
                    fixtureId: parsedIdent.eventID,
                    gameId: parsedIdent.gameID,
                    marketName: eventGameData.gameName,
                    tipName: odd.nameForTicket ? odd.nameForTicket : odd.name,
                    fixtureName: eventGameData.eventName,
                    sportName: eventGameData.gameName,
                    rate: parsedIdent.rate,
                    rateAccepted: parsedIdent.rate,
                    indent: ident,
                    number: eventGameData.number,
                    errors: [],
                    offerType: eventGameData.offerType,
                    canAnimate: canAnimate
                }));
            }
            evaluateAndUpdateOfferType();
            if (self.isSystem) {
                updateBets(evaluatePossibleBets(), false);
            }
            if (self.isOTBSettingSet) {
                return ticketPayAsync();
            }
            else {
                if (!preventModifyOdds) {
                    return validateTicketAsync({ showUpdateError: false, sourceForLog: 'addTipAsync' + ident });
                }
            }
            return getSucceededPromiseAsync();
        };

        /**
         * Removes a row from coupon in state allowing canModifyOdds. Otherwise does nothing.
         */
        const removeTipAsync: (ident: string, preventModifyOdds?: boolean) => Promise<void> = flow(function* (ident: string, preventModifyOdds?: boolean) {
            if (!self.canValidateTicket) {
                throw new Error("Cannot change coupon");
            }
            let parseIdent = parseTipIdent(ident);
            let selectionId = parseIdent.tipID;
            const selection = self.selections.find(tip => tip.id === selectionId); //createOddKeyForRemove(tip.ident) === createOddKeyForRemove(ident));
            if (selection) {
                if (self.selections.remove(selection)) {
                    evaluateAndUpdateOfferType();
                    if (self.isSystem) {
                        if (self.selections.length >= minimumSelectionsForSystem) {
                            updateBets(evaluatePossibleBets(), false);
                        } else {
                            //less than 3 => switch to simple ! TIPOS-7338
                            yield setTicketTypeAsync(TicketType.Simple, true);
                        }
                    }
                }

                if (self.selections.length === 0) {
                    setErrors([]);
                }

                if (!preventModifyOdds) {
                    yield validateTicketAsync({ showUpdateError: false, sourceForLog: 'removeTipAsync ' + ident });
                }
            }
        });

        /**
         * Cancel previous validateTicket request, see comment at validateTicketCtxWrapper why it needs to wait.
         */
        const cancelValidateTicketAsync: () => Promise<void> = flow(function* () {
            if (validateTicketCtxWrapper != null) {
                env.loggingService.info("validateTicket canceled");
                yield validateTicketCtxWrapper.cancel('Load has been canceled.');
            }
        });

        const validateTicketAsync: (params: IValidateTicketAsyncParams) =>
            Promise<void> = flow(function* (params: IValidateTicketAsyncParams) {
                if (self.isValidateTicketInProgress && params.skipIfAlreadyInProgress === true) {
                    return;
                }

                if (!self.canValidateTicket) {
                    throw new Error("Unable to call validateTicket");
                }

                self.id = null; //something has changed, reset id for hint
                self.hint = null;
                self.hintForUser = null;

                if (self.selections.length == 0) { //do nothing
                    clearSummary();
                    storeTicket(false);
                    return;
                }

                let started = new Date();
                let telemetryDetail = JSON.stringify({ src: params.sourceForLog });

                let myCxt: ModifyOddsCtxWrapper;
                try {
                    yield cancelValidateTicketAsync();

                    self.isValidateTicketInProgress = true;

                    myCxt = new ModifyOddsCtxWrapper(env.api.open());
                    validateTicketCtxWrapper = myCxt;

                    storeTicket(false);

                    // env.loggingService.info('------------------------------');
                    // env.loggingService.info('request', ticketDataToApi(false));
                    const response: ITicketResponse = yield myCxt.validateTicketCancelableAsync(ticketDataToApi(false, true)); //TODON maybe not necessary
                    preprocessApiErrorsInTicket(response);
                    env.loggingService.info('ticket validate response', response);
                    env.telemetryService.logAjaxRequest(TelemetryServiceDataType.CouponReset, env.loggedUserId, started, telemetryDetail);

                    if (validateTicketCtxWrapper === myCxt) {
                        updateTicket(response);
                        storeTicket(true);
                    }
                } catch (error) {
                    env.telemetryService.logAjaxFailedRequest(TelemetryServiceDataType.CouponReset, env.loggedUserId, started, telemetryDetail, error);
                    if (errorIsCancel(error)) {
                        throw error;
                    }
                    if (params.showUpdateError) {
                        setErrors([translation().coupon_CouponGeneralError]);
                    }
                    if (errorIsSessionExpired(error)) {
                        if (self.isSystem) { //TODON wtf
                            setErrors([translation().general_SessionExpired]);
                        }
                    }
                    throw error;
                }
                finally {
                    if (validateTicketCtxWrapper === myCxt) {
                        self.isValidateTicketInProgress = false;
                        validateTicketCtxWrapper.close();
                        validateTicketCtxWrapper = null;
                    }
                }
            });

        const storeTicket = (canStoreSummary: boolean): void => {
            if (self.isOTBSettingSet) {
                return;
            }
            try {
                env.ticketStorage.store(env.ticketStorage.getKey(env.loggedUserId, self.index), ticketDataToStorageData(canStoreSummary));
            }
            catch (e) {
                env.loggingService.error(e);
            }
        }

        const ticketDataToApi = (isForTicketPayToAcceptHint: boolean, isForTicketValidate: boolean): ITicketValidateParams => {
            let ticketSelections: ITicketSelectionRequest[] = [];
            self.selections.forEach(selection => {
                ticketSelections.push({
                    Id: selection.id,
                    FixtureId: selection.fixtureId,
                    OfferType: selection.offerType,
                    Rate: isForTicketValidate ? selection.rate : selection.rateAccepted,
                    IsBanker: selection.isBanker ? true : false,
                });
            });

            if (isForTicketPayToAcceptHint && self.isSystem) {
                throw new Error("Not allowed");
            }

            let accType = self.availableAccountTypes.find(t => t.areKeysEqual(self.selectedAccountType));
            if (self.isSystem) {
                if (accType && !accType.isMoney) {
                    throw new Error("Unexpected account type with points");
                }
            }

            let bets: ITicketBetRequest[] = [];
            if (self.ticketType == TicketType.Simple) {
                bets.push({ Stake: self.stake.value, SelectedSystems: [ticketSelections.length] });
            } else if (self.ticketType == TicketType.System) {
                //see also isValidStakeInputs
                if (self.stakeType == StakeType.Total || self.stakeType == StakeType.Unit) {
                    self.bets.forEach(bet => {
                        if (bet.isChecked) {
                            if (!bet.stake.isValid) {
                                throw new Error("Unexpectedly invalid stake"); //expected user friendly stop in isValidStakeInputs
                            }
                            bets.push({ Stake: bet.stake.value, SelectedSystems: [bet.id] });
                        }
                    });
                } else {
                    bets.push({
                        Stake: self.stake.value,
                        SelectedSystems: self.bets.filter(bet => bet.isChecked).map(bet => bet.id)
                    });
                }
            } else {
                throw new Error("Unexpected ticket type " + self.ticketType)
            }
            let params: ITicketValidateParams = {
                Ticket: {
                    HintRefCouponId: isForTicketPayToAcceptHint && self.id !== "" ? self.id : undefined,
                    ProposedStake: isForTicketPayToAcceptHint ? self.hint : undefined,
                    StakeType: self.stakeType,
                    TicketType: self.ticketType,
                    OfferType: self.offerType,
                    OddsChangeType: self.oddsChangeType,
                    TicketSelections: ticketSelections,
                    Bets: bets,
                    AMLOptions: amlDataToApi()
                },
                AccountTypeId: self.isSystem ? null : tailorAccountTypeForUnitedTicket(self.selectedAccountType, self.offerType), //for System there is only one options (not shown and sent)
                FreeTicketId: self.selectedAccountType ? self.selectedAccountType.freeTicketId : null,
                DoNotReportDifferentRate: isForTicketValidate
            };
            return params;
        }

        /**
         * Change account type from prematch to live if ticket changes from prematch to live and vice versa.
         * Live has just two products.
        */
        const tailorAccountTypeForUnitedTicket = (accountType: { id: string, freeTicketId?: number | Long }, offerType: OfferType): string => {

            if (!accountType) {
                return "";
            }

            if (__IsMTSTipos__) {   //TODOHMTS
                if (offerType === OfferType.Live) {
                    if (accountType.id === "10007") { //initial, money
                        return "10008";
                    }
                }
                if (offerType === OfferType.Prematch) {
                    if (accountType.id === "10008") { //initial, money
                        return "10007";
                    }
                }
            }
            else {
                if (offerType === OfferType.Live) {
                    if (accountType.id === "10006") { //initial, money
                        return "10008";
                    }
                    if (accountType.id === "10011") { //points
                        return "10012";
                    }
                }
                if (offerType === OfferType.Prematch) {
                    if (accountType.id === "10008") { //initial, money
                        return "10006";
                    }
                    if (accountType.id === "10012") { //points
                        return "10011";
                    }
                }
            }
            return accountType.id;
        }

        const ticketDataToStorageData = (canStoreSummary: boolean): ITicketStorageData => {
            let ticketSelections: ITicketStorageSelection[] = [];
            self.selections.forEach(selection => {
                ticketSelections.push({
                    id: selection.id,
                    fixtureId: selection.fixtureId,
                    gameId: selection.gameId,
                    rate: selection.rateAccepted,
                    marketName: selection.marketName,
                    tipName: selection.tipName,
                    isBanker: selection.isBanker,
                    sportName: selection.sportName,
                    fixtureName: selection.fixtureName,
                    fixtureDate: selection.fixtureDate,
                    indent: selection.indent,
                    number: selection.number,
                    offerType: selection.offerType
                });
            });
            let bets: ITicketStorageBet[] = [];
            if (self.ticketType === TicketType.Simple) {
                bets.push({ stake: self.stake.value, id: ticketSelections.length });
            } else {
                if (self.ticketType === TicketType.System) {
                    self.bets.forEach(bet => {
                        if (bet.isChecked && bet.stake) {
                            bets.push({ stake: bet.stake.value, id: bet.id, unitStake: bet.unitStake });
                        }
                    });
                } else {
                    throw new Error("Unexpected ticket type " + self.ticketType);
                }
            }
            return {
                stored: new Date(),
                stakeType: self.stakeType,
                ticketType: self.ticketType,
                oddsChangeType: self.oddsChangeType,
                selections: ticketSelections,
                bets: bets,
                prizePotential: canStoreSummary ? self.potentialWin : null,
                totalRate: canStoreSummary ? self.rate : null,
                stake: self.stake.value,
                stakeMoneyForInfo: self.stakeMoneyForInfo,
                productFee: canStoreSummary ? self.productFee : null,
                oddsRateAdvantage: canStoreSummary ? self.oddsRateAdvantage : null,
                rateAdvantageFactor: canStoreSummary ? self.rateAdvantageFactor : null,
                availableAccountTypes: self.availableAccountTypes ? self.availableAccountTypes
                    .map(accountType => { return { key: accountType.key, name: accountType.name, isMoney: accountType.isMoney, hint: accountType.hint, stakeValue: accountType.stakeValue, canBeUsedByPlayer: accountType.canBeUsedByPlayer } }) : null,
                selectedAcountType: self.selectedAccountType,
                handlingFeeValue: self.handlingFeeValue,
                handlingFeePointsValue: self.handlingFeePointsValue
            };
        }

        const amlDataToApi = (): IAmlOptionsRequest | null => {
            if (!self.amlOptions || self.amlOptions.items.length == 0) {
                return null;
            }
            const selected: number[] = [];
            const userData: { Key: string, Value: string }[] = [];
            self.amlOptions.items.forEach(aml => {
                if (aml.isSelected) {
                    selected.push(aml.id);
                }
                if (aml.subItemText) {
                    userData.push({ Key: `text_${aml.id}`, Value: aml.subItemText.content });
                }
                if (aml.subItem1OfN) {
                    if (aml.subItem1OfN.selectedId) {
                        userData.push({ Key: `1OfN_${aml.id}`, Value: aml.subItem1OfN.selectedId.toString() });
                    }
                }
            });
            return {
                Selected: selected,
                UserData: userData
            }
        }

        /**
         * Sets couponType value and sets isSystem.
         */
        const setTicketType = (ticketType: TicketType): void => {
            self.ticketType = ticketType;
        }

        const updateTicket = (data: ITicketResponse): void => {
            env.loggingService.info("mtsTicket, updateTicket", data);
            if (data.Selections) {
                data.Selections.forEach(sourceSel => {
                    const found = self.selections.find(sel => sourceSel.Base.Id.toString() == sel.id);
                    if (found) {
                        if (canAutoUpdateRates) {
                            rateChanged(found, sourceSel.RateCurrent);
                        } else {
                            found.rate = sourceSel.RateCurrent;
                        }
                    }
                });
            }


            const amlOrigSelected: Set<number> = new Set<number>();
            if (self.amlOptions) {
                self.amlOptions.items.forEach(item => {
                    if (item.isSelected) {
                        amlOrigSelected.add(item.id);
                    }
                })
            }

            // process AML, use current values to override choosen data from api since it may be old
            const amlOrigTexts: Map<number, string> = new Map<number, string>();
            if (self.amlOptions) {
                self.amlOptions.items.forEach(item => {
                    if (item.subItemText) {
                        amlOrigTexts.set(item.id, item.subItemText.content);
                    }
                });
            }

            const amlOrig1OfN: Map<number, number> = new Map<number, number>();
            if (self.amlOptions) {
                self.amlOptions.items.forEach(item => {
                    if (item.subItem1OfN) {
                        amlOrig1OfN.set(item.id, item.subItem1OfN.selectedId);
                    }
                });
            }

            self.amlOptions.items.clear();
            if (data.AMLOptions && data.AMLOptions.length > 0) {
                data.AMLOptions.forEach(aml => {

                    let textStore: Instance<typeof AMLSubOptionText> = null;
                    if (aml.Text) {
                        textStore = AMLSubOptionText.create(<SnapshotOut<typeof AMLSubOptionText>>{
                            label: aml.Text.Title,
                            content: amlOrigTexts.has(aml.Id) ? amlOrigTexts.get(aml.Id) : ""
                        });
                    }

                    let subItem1OfNData: Instance<typeof AMLSubOption1OfN> = null;

                    let subItem1OfNItems: Instance<typeof AMLSubOption1OfNItem>[] = null;
                    if (aml.Sub1OfN && aml.Sub1OfN.length > 0) {
                        let selectedId: number | null = null;
                        subItem1OfNItems = aml.Sub1OfN.map(item => {
                            if ((amlOrig1OfN.has(aml.Id) && amlOrig1OfN.get(aml.Id) === item.Id)) {
                                selectedId = item.Id;
                            }
                            return AMLSubOption1OfNItem.create(<SnapshotOut<typeof AMLSubOption1OfNItem>>{
                                id: item.Id,
                                label: item.Name
                            })
                        });

                        if (aml.WidgetType == AMLWidgetType.Select && selectedId === null) {
                            selectedId = 0;
                        }

                        subItem1OfNData = AMLSubOption1OfN.create(<SnapshotOut<typeof AMLSubOption1OfN>>{
                            selectedId: selectedId,
                            items: subItem1OfNItems
                        });
                    }

                    let amlStore = AMLOptionItem.create(<SnapshotOut<typeof AMLOptionItem>>{
                        id: aml.Id,
                        label: aml.Name,
                        widgetType: aml.WidgetType ? aml.WidgetType : AMLWidgetTypeUI.Default,
                        textAtEnd: aml.TextAtEnd,
                        isSelected: amlOrigSelected.has(aml.Id),
                        subItemText: textStore,
                        subItem1OfN: subItem1OfNData,
                        disabledIfDef: {},
                        isDisabled: false
                    });
                    if (aml.DisabledIf) {
                        amlStore.setDisableIfDefFromApi(aml.DisabledIf);
                    }
                    self.amlOptions.items.push(amlStore);
                });
                self.amlOptions.updateDisabled();
            }

            if (!data.Base.Errors || data.Base.Errors.length == 0) {
                self.potentialWin = data.Base.PrizeEvalPotentionalValue;
                self.rate = data.Base.Rate;

                if (self.ticketType == TicketType.System) {
                    self.stakeWhole = data.Base.Stake;
                    self.stake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{
                        isValid: true,
                        value: data.Base.Stake
                    });
                }
                if (data.Base.Bets && self.bets?.length) { //TODOP weird
                    data.Base.Bets.forEach(betData => {
                        if (self.bets[betData.SelectedSystem - 1]) {
                            self.bets[betData.SelectedSystem - 1].stake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{
                                isValid: true,
                                value: self.stakeType === StakeType.Unit ? betData.UnitStake : betData.SystemStake
                            });
                            self.bets[betData.SelectedSystem - 1].unitStake = betData.UnitStake;
                        }
                    })
                }
            }
            else {
                clearSummary();
            }

            if (data.Base.ProductFee) {
                self.productFee = MoneyWithCurrency.create(<SnapshotOut<typeof MoneyWithCurrency>>{
                    amount: data.Base.ProductFee.Amount, currency: data.Base.ProductFee.IsMoney ? currency : pointsCurrency
                });
            } else {
                self.productFee = null;
            }
            self.oddsRateAdvantage = data.Base.OddsRateAdvantage ? data.Base.OddsRateAdvantage.Value : null;
            self.rateAdvantageFactor = data.Base.RateAdvantageFactor ? data.Base.RateAdvantageFactor.Value : null;
            self.handlingFeeValue = data.Base.HandlingFeeValue;
            self.handlingFeePointsValue = data.Base.HandlingFeePointsValue;
            self.maxStake = data.Base.StakeMax ? data.Base.StakeMax.Value : null;

            self.selections.forEach(selection => {
                if (selection.errors.length > 0) {
                    selection.errors = observable([]);
                }
            });
            let globalErrors: string[] = [];
            if (data.Base.Errors) {
                data.Base.Errors.forEach(error => {
                    if (error.SelectionId) {
                        const errSelectionId = error.SelectionId.toString();
                        let foundSelection = self.selections.find(selection => selection.id === errSelectionId);
                        if (foundSelection) {
                            foundSelection.errors.push(error.Text);
                        }
                    } else {
                        globalErrors.push(error.Text);
                    }

                });
            }

            setErrors(globalErrors);

            if (globalErrors.length == 0) { //change account types when there is no error only not to reset type to default in case e.g rate change

                let isMoney = self.isStakeAsMoney;
                if (data.AvailableAccountTypes) {
                    self.availableAccountTypes = observable([]);
                    data.AvailableAccountTypes.forEach(accountType => {

                        if (accountType.FreeTickets) {
                            accountType.FreeTickets.Items.forEach(freeTicket => {
                                self.availableAccountTypes.push(AvailableAccountType.create(<SnapshotOut<typeof AvailableAccountType>>{
                                    key: AccountTypeKey.create(<SnapshotOut<typeof AccountTypeKey>>{ id: accountType.Id, freeTicketId: freeTicket.Id }),
                                    name: freeTicket.Name,
                                    isMoney: true,
                                    hint: freeTicket.Hint,
                                    stakeValue: freeTicket.StakeValue,
                                    canBeUsedByPlayer: freeTicket.CanBeUsedByPlayer
                                }));
                            });
                        }
                        else {
                            self.availableAccountTypes.push(AvailableAccountType.create(<SnapshotOut<typeof AvailableAccountType>>{
                                key: AccountTypeKey.create(<SnapshotOut<typeof AccountTypeKey>>{ id: accountType.Id, freeTicketId: null }),
                                name: accountType.Name,
                                isMoney: accountType.IsMoney,
                                hint: null,
                                stakeValue: null,
                                canBeUsedByPlayer: true
                            }));
                        }
                    });



                    if (self.selectedAccountType) {
                        let selectedAccountType = self.availableAccountTypes.find(item => item.areKeysEqual(self.selectedAccountType));
                        if (!selectedAccountType || !selectedAccountType.canBeUsedByPlayer) {
                            self.selectedAccountType = null;
                        }
                    }

                    if (!self.selectedAccountType && data.AvailableAccountTypes.length) {
                        if (self.availableAccountTypes[0]) {
                            self.selectedAccountType = AccountTypeKey.create({ ...self.availableAccountTypes[0].key });
                        }
                    }
                } else {
                    if (self.availableAccountTypes.length) {
                        self.availableAccountTypes = observable([]);
                    }
                    self.selectedAccountType = null;
                }
                fixStakeAfterChangeOfAccountType(isMoney);
            }
        };

        const clearSummary = (): void => {
            self.potentialWin = null;
            self.rate = null;
        }

        const factorial = (num: number): number => { //TODOH may be precomputed for some values
            var rval = 1;
            for (var i = 2; i <= num; i++)
                rval = rval * i;
            return rval;
        }

        const evaluatePossibleBets = (): { id: number, title: string, combinationCount: number }[] => {
            let groupsCount = self.selections.filter(item => !item.isBanker).length;
            let result = [];
            for (let i = 1; i <= groupsCount; i++) {
                result.push({ id: i, title: i + "/" + groupsCount, combinationCount: factorial(groupsCount) / (factorial(groupsCount - i) * factorial(i)) });
            }
            return result;
        }

        const createNewStake = (betCombinationCount: number): number => {
            return self.stakeType === StakeType.Unit ? env.settingsFromApi.combiStakeMin : roundStakeInCombi(env.settingsFromApi.combiStakeMin * betCombinationCount);
        }

        const updateBets = (possibleBets: { id: number, title: string, combinationCount: number }[], canCheckFirst: boolean): void => {
            if (self.bets) {
                for (let i = self.bets.length - 1; i >= 0; i--) {
                    const findCnt = possibleBets.filter(item => self.bets[i].id === item.id).length;
                    if (!findCnt) {
                        self.bets.splice(i, 1);
                    }
                }
            }

            possibleBets.forEach((bet, index) => {
                let existing = self.bets && self.bets.find(bb => bb.id === bet.id);
                let newStake = null;
                if (existing) {
                    if (existing.stake) {
                        if (existing.isChecked) {
                            newStake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{ isValid: existing.stake.isValid, value: existing.stake.value });
                        }
                    } else {
                        if (existing.isChecked) {
                            newStake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{
                                isValid: true,
                                value: createNewStake(bet.combinationCount)
                            });
                        }
                    }
                    existing.stake = newStake;
                    existing.title = bet.title;
                    existing.combinationCount = bet.combinationCount;
                } else {
                    if (!self.bets) {
                        self.bets = observable([]);
                    }
                    const isChecked = canCheckFirst && index === 0;
                    const newBet = TicketBet.create(<SnapshotOut<typeof TicketBet>>{
                        id: bet.id,
                        title: bet.title,
                        isChecked: isChecked,
                        stake: isChecked ? { isValid: true, value: createNewStake(bet.combinationCount) } : null,
                        combinationCount: bet.combinationCount,
                    });
                    self.bets.push(newBet);
                }
            })
        }

        const powXForStakeInCombi = Math.pow(10, env.settingsFromApi.ticketStakeDecimalPlaces);
        /**
         * Protection against adding 0,01 in values.
         * @param stake
         * @returns
         */
        const roundStakeInCombi = (stake: number): number => {

            return Math.floor(stake * powXForStakeInCombi) / powXForStakeInCombi;
        }

        const setTicketTypeAsync = (ticketType: TicketType, isForceFromApi: boolean = false): Promise<void> => {
            if (ticketType == TicketType.System) {
                if (self.ticketType != TicketType.System && self.canSetSystemTicketType) {
                    updateBets(evaluatePossibleBets(), true);
                    self.bets[0].isChecked = true;
                    self.ticketType = TicketType.System;
                    self.stakeType = StakeType.All;
                    let isMoney = self.isStakeAsMoney;
                    self.selectedAccountType = null;
                    self.availableAccountTypes.clear();
                    fixStakeAfterChangeOfAccountType(isMoney);
                    processBetStakes();
                    return validateTicketAsync({ showUpdateError: true, sourceForLog: "setTicketTypeAsync " + ticketType });
                } else {
                    return getFailedPromiseAsync(new Error("Already system ticket or cannot change"));
                }
            } else {
                if (self.isSystem) {
                    if (!isForceFromApi && self.isSystemTicketUpdating) {
                        return getFailedPromiseAsync(new Error("Some operation in progress"));
                    }
                    setTicketType(TicketType.Simple); //there are more tips
                    self.stakeType = StakeType.All;
                    self.bets = null;
                    //groupNames are irrelevant
                    if (isForceFromApi) {
                        return getSucceededPromiseAsync();
                    }
                    return validateTicketAsync({ showUpdateError: true, sourceForLog: "setTicketTypeAsync " + ticketType });
                } else {
                    return getFailedPromiseAsync(new Error("Already non system coupon"));
                }
            }
        };

        const prepareAndStoreAuxValues = (): void => {
            self.auxIsSystem = self.isSystem;
            self.auxBetStorageData = JSON.stringify(ticketDataToStorageData(true));
        };

        const setErrors = (errors: string[], showAboveSubmitButtons = false): void => {
            if (showAboveSubmitButtons) {
                self.errors = observable([]);
                self.errorsAboveSubmitButtons = observable(errors);
            } else {
                self.errors = observable(errors);
                if (self.errorsAboveSubmitButtons && self.errorsAboveSubmitButtons.length) {
                    self.errorsAboveSubmitButtons = null;
                }
            }
        }

        const prepareJustSentTicketCopyAsync: () => Promise<void> = flow(function* () {
            if (!self.isFinalTicketState) {
                throw new Error("Coupon not in final state");
            }

            if (!self.auxBetStorageData) {
                throw new Error("self.auxBetStorageData is empty");
            }

            var data: ITicketStorageData = <ITicketStorageData>JSON.parse(self.auxBetStorageData);
            try {
                setDataFromStorage(data);
            }
            catch (e) {
                yield clearCouponAsync(true);
                setErrors([translation().coupon_CopyGeneralError]);
                return;
            }
            self.auxIsSystem = null;
            self.auxBetStorageData = null;
            self.ticketState = TicketStatusType.New;
            yield validateTicketAsync({ showUpdateError: true, sourceForLog: "prepareJustSentTicketCopyAsync", skipIfAlreadyInProgress: false });
        });

        /**
         * Accepts changed rates and calls modifyodds to update the coupon.
         */
        const acceptChangedRatesAndEvaluateAsync: () => Promise<void> = flow(function* () {
            if (!self.isValidStakeInputs) {
                setErrors([translation().coupon_StakeInputError], true);
                return;
            }

            self.isAcceptChangedRatesAndEvalInProgress = true;
            try {
                self.selections.forEach(tip => { tip.rateAccepted = tip.rate; tip.rateChange = RateChange.ForceNone });
                yield validateTicketAsync({ showUpdateError: true, sourceForLog: "acceptChangedRatesAndEvaluateAsync" });
            }
            finally {
                self.isAcceptChangedRatesAndEvalInProgress = false;
            }
        });

        const setIsBankerAsync = (selection: ITicketSelection, value: boolean) => {
            //let selection = self.selections.find(item => item.id === tipId);
            if (!self.isSystem) {
                throw new Error("For System ticket only");
            }
            selection.setIsBanker(value);
            updateBets(evaluatePossibleBets(), false);
            return validateTicketAsync({ showUpdateError: false, sourceForLog: "setIsBankerAsync " + value });
        };

        const setStakeTypeAsync = (stakeType: StakeType): Promise<void> => {
            const prevStakeType = self.stakeType;

            if (!self.isSystem) {
                throw new Error('Ticket not as System');
            }
            if (prevStakeType === stakeType) {
                return getSucceededPromiseAsync();
            }
            self.stakeType = stakeType;
            if (stakeType == StakeType.All) {
                if (prevStakeType === StakeType.Total) {
                    self.bets.forEach((bet: ITicketBet) => {
                        if (bet.isChecked) {
                            bet.stake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{ isValid: true, value: bet.stake.value });
                        }
                    });
                }
                else {
                    self.bets.forEach((bet: ITicketBet) => {
                        bet.stake = null;
                    });
                }
            }
            if (stakeType == StakeType.Total && prevStakeType == StakeType.Unit) {
                self.bets.forEach((bet: ITicketBet) => {
                    if (bet.isChecked) {
                        bet.stake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{ isValid: true, value: roundStakeInCombi(bet.combinationCount * bet.unitStake) });
                    }
                });
            }
            if (stakeType == StakeType.Unit) {
                let checkedNumber = self.bets.filter((bet: ITicketBet) => bet.isChecked).length;
                if (checkedNumber == 0) {
                    self.bets[0].toggleSelect();
                    checkedNumber = 1;
                }
                //let unitStake = Math.round(self.stake.value * 100 / checkedNumber) / 100;
                self.bets.forEach((bet: ITicketBet) => {
                    if (bet.isChecked) {
                        bet.stake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{ isValid: true, value: bet.unitStake });
                    }
                });
            }

            return validateTicketAsync({ showUpdateError: true, sourceForLog: "setStakeTypeAsync " + stakeType });
        };

        const prepareTicketAsync: () => Promise<void> = flow(function* () {
            yield prepareTicketRawAsync();
        });

        const prepareTicketRawAsync: () => Promise<void> = flow(function* () {
            try {
                let ctx: IGroupedApiCtx<ISportsBettingBaseApi> = env.api.open();
                setErrors([]);
                prepareAndStoreAuxValues();
                const response: ITicketPrepareResponse = yield ctx.api.ticketPrepareAsync(ticketDataToApi(false, false));
                response.Ticket = preprocessApiErrorsInTicket(response.Ticket);
                if (response.ResultType == ResultType.Success) {
                    yield clearCouponAsync(true);
                    self.ticketState = TicketStatusType.Saved;
                    self.savedTicketNumber = response.Number;
                    self.savedTicketExpiration = protoUtils.protoTimeStamp2Date(response.ExpirationDate);
                    env.ticketStorage.remove(env.ticketStorage.getKey(0, 0));
                    return;
                }
                if (response.Ticket && response.Ticket.Base.Errors && response.Ticket.Base.Errors.length) {
                    updateTicket(response.Ticket);
                    self.ticketState = TicketStatusType.New;
                    return;
                }
                setErrors([translation().coupon_CouponGeneralError]);
            } catch (error) {
                if (errorIsCancel(error)) {
                    throw error;
                }
                setErrors([translation().coupon_CouponGeneralError]);
                throw error;
            }
            finally {
            }

        });

        const ticketPayAsync: () => Promise<void> = flow(function* () {
            yield ticketPayRawAsync(false);
        });

        const ticketPayRawAsync: (isFromHint: boolean) => Promise<void> = flow(function* (isFromHint: boolean) {
            if (!self.canPayOrSave && !isFromHint) {
                throw new Error("Unable to pay or save coupon");
            }

            if (self.isLoggedUser && !self.isValidStakeInputs) {
                setErrors([translation().coupon_StakeInputError], true);
                return;
            }

            if (isFromHint) {
                self.isAcceptingHintInProgress = true;
            }
            else {
                if (self.isLoggedUser) {
                    self.ticketState = TicketStatusType.Pay; // hack to show "sending to pay" while modifyOdds is waiting to finish
                }
            }
            try {

                let started = new Date();

                yield cancelValidateTicketAsync();

                if (!self.isLoggedUser) { // I need to wait to cancel to show my error
                    setErrors([translation().coupon_LoginForSend], true);
                    return;
                }

                try {
                    let ctx: IGroupedApiCtx<ISportsBettingBaseApi> = env.api.open();

                    if (self.offerType === OfferType.Live) {
                        self.waitingInPayProgress = env.settingsFromApi.timeForCoupon / self.liveCouponPayWaitStepMs;
                    }

                    setErrors([]); //remove old errors not to confuse user during paying

                    const response: ITicketConfirmResponse = yield ctx.api.ticketPayAsync(ticketDataToApi(isFromHint, false));
                    response.Ticket = preprocessApiErrorsInTicket(response.Ticket);
                    self.waitingInPayProgress = null;

                    let telemetryDetail: string;
                    if (response.ResultType == ResultType.Success) {
                        telemetryDetail = JSON.stringify({ acc: true, no: response.Ticket.Base.Number, otb: self.isOTBSettingSet });
                    } else if (response.ResultType == ResultType.FailWithHint) {
                        telemetryDetail = JSON.stringify({ rt: response.ResultType, h: response.ProposedStake, otb: self.isOTBSettingSet });
                    } else {
                        telemetryDetail = JSON.stringify({ rt: response.ResultType, e: response.Ticket.Base.Errors, otb: self.isOTBSettingSet });
                    }
                    env.telemetryService.logAjaxRequest(TelemetryServiceDataType.CouponPay, env.loggedUserId, started, telemetryDetail);

                    if (env.settings.isDebug && ((isFromHint && self.ticketState !== TicketStatusType.Hint) || (!isFromHint && self.ticketState !== TicketStatusType.Pay))) {
                        env.loggingService.error("TicketState is not pay (" + self.ticketState + ") when processing the result of ticketPay");
                        throw new Error("TicketState is not pay when processing the result of ticketPay");
                    }

                    if (self.isOTBSettingSet) {
                        env.loggingService.info(`Ga event sent succ: ${response.ResultType == ResultType.Success}`);
                        gAService.sendEvent(
                            response.ResultType == ResultType.Success ? "One Tap Bet OK" : "One Tap Bet Error",
                            'sent',
                            response.Ticket && response.Ticket.Base ? response.Ticket.Base.Number : "unknown"
                        );
                    }

                    if (response.ResultType == ResultType.Success) {
                        prepareAndStoreAuxValues();
                        let wasLiveBet = self.offerType === OfferType.Live;
                        yield clearCouponAsync(true);
                        self.ticketState = TicketStatusType.Accepted;
                        env.ticketStorage.remove(env.ticketStorage.getKey(env.loggedUserId, 0));
                        env.couponSent({ isLive: wasLiveBet });
                        return;
                    }

                    if (response.ResultType == ResultType.FailWithHint) {
                        if (self.isSystem) {
                            throw new Error("Unexpected ticket type");
                        }
                        self.id = response.Ticket.Base.Id ? response.Ticket.Base.Id.toString() : null;
                        self.ticketState = TicketStatusType.Hint;
                        let isMoney = self.isStakeAsMoney;
                        if (response.ProposedStakeRounded.IsMoney != isMoney) {
                            throw new Error("Unexpected currency");
                        }
                        self.hint = response.ProposedStake.Amount; //is always in money, but generally not important, just sent if user agrees
                        self.hintForUser = response.ProposedStakeRounded.Amount;

                        self.isAcceptingHintInProgress = false;
                        return;
                    }

                    if (response.Ticket && response.Ticket.Base.Errors && response.Ticket.Base.Errors.length) {
                        updateTicket(response.Ticket);
                        self.ticketState = TicketStatusType.New;
                        return;
                    }

                    self.ticketState = TicketStatusType.New;
                    setErrors([translation().coupon_CouponGeneralError]);
                } catch (error) {
                    if (errorIsCancel(error)) {
                        throw error;
                    }
                    self.ticketState = TicketStatusType.New;
                    self.waitingInPayProgress = null;
                    setErrors([translation().coupon_CouponGeneralError]);
                    throw error;
                }
            } finally {
                if (isFromHint) {
                    self.isAcceptingHintInProgress = false;
                } else {
                    if (self.ticketState == TicketStatusType.Pay) { //see setting above try
                        self.ticketState = TicketStatusType.New;
                        setErrors([translation().coupon_CouponGeneralError]);
                    }
                }
            }
        });

        const couponPayWithHintAsync: (isAccepted: boolean) => Promise<void> = flow(function* (isAccepted: boolean) {
            if (self.isSystem) {
                throw new Error("Unexpected ticket type");
            }
            if (self.ticketState != TicketStatusType.Hint) {
                throw new Error("Unexpected state");
            }

            if (self.isAcceptingHintInProgress) {
                env.loggingService.warning("MTSTicket.couponPayWithHintAsync called withing self.isAcceptingHintInProgress == true");
                return;
            }

            if (!isAccepted) {
                //prepareAndStoreAuxValues();
                setErrors([translation().coupon_SentRejectMessage]);
                self.ticketState = TicketStatusType.New;
                // env.couponSent({ isLive: self.offerType === OfferType.Live });
                return;
            }

            setStake(self.hintForUser);
            prepareAndStoreAuxValues();
            try {
                yield ticketPayRawAsync(true);
            }
            finally {
                self.id = null;
            }
        });

        const setBetStakeAsync = (bet: ITicketBet, value: number): Promise<void> => {
            // if (bet.stake.isValid && bet.stake.value === value) {
            //     return getSucceededPromiseAsync();
            // }
            if (self.stakeType === StakeType.Total) {
                if (value / bet.combinationCount < env.settingsFromApi.combiStakeMin) {
                    value = roundStakeInCombi(env.settingsFromApi.combiStakeMin * bet.combinationCount);
                }
            }
            bet.setStakeOrValidity(true, value);
            return validateTicketAsync({ showUpdateError: true, sourceForLog: "setBetStakeAsync " + bet.title + "/" + value });
        }

        const toggleBetSelectionAsync = (bet: ITicketBet): Promise<void> => {
            bet.toggleSelect();
            updateBets(evaluatePossibleBets(), false);
            processBetStakes();
            return validateTicketAsync({ showUpdateError: true, sourceForLog: "toggleCardinalitySelectionAsync " + bet.title });
        };

        const setOrToggleAcceptedRateChangeAsync = (rateChange: RateChangeAcceptType): Promise<void> => {
            let newValue = convertRateChangeAcceptType2OddsChangeType(rateChange);
            if (newValue === self.oddsChangeType) {
                self.oddsChangeType = OddsChangeType.None;
            } else {
                self.oddsChangeType = newValue;
            }
            if (newValue === OddsChangeType.Any) {
                self.selections.forEach(tip => { tip.rateAccepted = tip.rate; tip.rateChange = RateChange.ForceNone; });
            } else if (newValue === OddsChangeType.Higher) {
                self.selections.forEach(tip => { if (tip.rate > tip.rateAccepted) { tip.rateAccepted = tip.rate; tip.rateChange = RateChange.ForceNone; } });
            }
            return validateTicketAsync({ showUpdateError: true, sourceForLog: "setOrToggleAcceptedRateChange", skipIfAlreadyInProgress: true }); //to store the change, do not need wait for result
        };

        const convertRateChangeAcceptType2OddsChangeType = (rateChange: RateChangeAcceptType): OddsChangeType => {
            switch (rateChange) {
                case RateChangeAcceptType.None: return OddsChangeType.None;
                case RateChangeAcceptType.RateIncrease: return OddsChangeType.Higher;
                case RateChangeAcceptType.AnyRate: return OddsChangeType.Any;
            }
            throw new Error("Unexpected value " + rateChange);
        };

        const setAccountTypeAsync = (accountType: { id: string, freeTicketId?: number | Long }): Promise<void> => {
            let isMoney = self.isStakeAsMoney;
            self.selectedAccountType = AccountTypeKey.create(<SnapshotOut<typeof AccountTypeKey>>{ id: accountType.id, freeTicketId: accountType.freeTicketId });

            //Set stake?
            if (accountType.freeTicketId) {
                let currentAccountType = self.availableAccountTypes.find(accType => accType.areKeysEqual(self.selectedAccountType));
                setStake(currentAccountType.stakeValue);
                //TODO_FT: Zneaktivneni policka pro sazku + skryti predvybranych sazek
                //TODO_FT: Prepnuti z bodu na penize (test)
            }

            fixStakeAfterChangeOfAccountType(isMoney);
            return validateTicketAsync({ showUpdateError: false, sourceForLog: "setAccountTypeAsync" });
        }

        const pointsToMoneyDecimalsFactor = Math.pow(10, env.settingsFromApi.ticketStakeDecimalPlaces);
        const convertPointsToMoney = (stake: number): number => {
            let newStake = Math.floor(stake * pointsToMoneyDecimalsFactor / env.settingsFromApi.currencyToPointsRatio);
            if (pointsToMoneyDecimalsFactor > 1) {
                newStake = newStake / pointsToMoneyDecimalsFactor;
            }
            return newStake;
        }

        const fixStakeAfterChangeOfAccountType = (originalIsMoney: boolean) => {
            if (originalIsMoney !== self.isStakeAsMoney) {
                if (self.stake.isValid) {
                    let newStake: number;
                    if (self.isStakeAsMoney) { // true is change from points to money
                        newStake = convertPointsToMoney(self.stake.value);
                        if (newStake < env.settingsFromApi.stakeMin) {
                            newStake = env.settingsFromApi.stakeMin;
                        }
                        self.stakeMoneyForInfo = null;
                    } else {
                        newStake = Math.round(self.stake.value * env.settingsFromApi.currencyToPointsRatio);
                        if (newStake < env.settingsFromApi.stakeMin * env.settingsFromApi.currencyToPointsRatio) {
                            newStake = env.settingsFromApi.stakeMin * env.settingsFromApi.currencyToPointsRatio;
                        }
                        self.stakeMoneyForInfo = convertPointsToMoney(newStake);
                    }
                    self.stake = NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{ value: newStake, isValid: true });
                }
            }
        }

        /** Update money stake when stake in points is used */
        const fixStakeAfterChangeOfPoints = (): void => {
            if (!self.isStakeAsMoney && self.stake.isValid) {
                self.stakeMoneyForInfo = convertPointsToMoney(self.stake.value);
            }
        }

        /**
         * Expected for live with one tab
         */
        const getSelectionsToCompare = (): Map<string, number> => {
            let result = new Map<string, number>();
            if (!self.canModifyOdds) {
                return result;
            }
            self.selections.map(selection => {
                result.set(selection.id, selection.rate);
            });
            return result;
        }

        const ratesChanged = (items: { id: string, rate: number }[]): void => {
            if (!self.canModifyOdds || !items.length) {
                return;
            }
            let wasChange = false;
            items.forEach(item => {
                let selection = self.selections.find(sel => sel.id === item.id);
                if (selection) {
                    wasChange = wasChange || rateChanged(selection, item.rate);
                }
            });
            if (wasChange) {
                clearSummary();
                validateTicketAsync({ showUpdateError: false, sourceForLog: "ratesChanged", skipIfAlreadyInProgress: false }); // do not need to wait for result
            }
        }

        const rateChanged = (selection: ITicketSelection, newRate: number): boolean => {
            let wasChange = false;
            env.loggingService.info("rateChanged ", selection.marketName, selection.fixtureName, selection.rate, newRate)
            if (newRate > selection.rate) {
                selection.rateChange = RateChange.Up;
                wasChange = true;
            } else if (newRate < selection.rate) {
                selection.rateChange = RateChange.Down;
                wasChange = true;
            } else {
                selection.rateChange = null;
            }
            if (self.oddsChangeType === OddsChangeType.Any ||
                (self.oddsChangeType === OddsChangeType.Higher && newRate > selection.rateAccepted)) {
                selection.rateAccepted = newRate;
            }
            selection.rate = newRate;
            return wasChange;
        }

        const preprocessApiErrorsInTicket = (ticket: ITicketResponse | null): ITicketResponse | null => {
            if (!ticket || !ticket.Base) {
                return ticket;
            }

            var foundGeneral = false;
            var foundSpecific = false;

            if (ticket.Base.Errors && ticket.Base.Errors.length) {
                ticket.Base.Errors.some(err => {
                    if (err.SelectionId) {
                        foundSpecific = true;
                    } else {
                        foundGeneral = true;
                    };
                    return foundGeneral && foundSpecific;
                });

                if (foundSpecific && !foundGeneral) {
                    ticket.Base.Errors.push({ Text: translationForMts().coupon_commonError, Type: TicketErrorType.Other });
                }
            }
            return ticket;
        }

        const setIsOTBSettingSet: (isSet: boolean) => Promise<void> = flow(function* (isSet: boolean) {
            if (!isSet) {
                setErrors([]);
                self.isOTBSettingSet = false;
                yield clearCouponAsync(true);
            }
            else {
                try {
                    let ctx: IGroupedApiCtx<ISportsBettingBaseApi> = env.api.open();

                    setErrors([]);
                    self.ticketState = TicketStatusType.Saving;
                    if (isSet) {
                        const response: ITicketOneTapBetStartResponse = yield ctx.api.ticketOneTapBetStartAsync(self.stake.value);
                        self.selections = observable([]);
                        if (response.Errors?.length) {
                            setErrors(response.Errors.map(error => error.Text));
                        }
                        else {
                            self.isOTBSettingSet = true;
                        }
                    }

                } catch (error) {
                    setErrors([translation().coupon_CouponGeneralError]);
                }
                finally {
                    self.ticketState = TicketStatusType.New;
                }
            }
        });

        return {
            setDataFromStorage,
            clearCouponAsync,
            addTipAsync,
            removeTipAsync,
            validateTicketAsync,
            ticketDataToApi,
            setTicketTypeAsync,
            prepareJustSentTicketCopyAsync,
            acceptChangedRatesAndEvaluateAsync,
            setIsBankerAsync,
            setStakeOrValidity,
            stakeChangedAsync,
            minimizeStake,
            prepareTicketAsync,
            ticketPayAsync,
            setStakeTypeAsync,
            setBetStakeAsync,
            toggleBetSelectionAsync,
            couponPayWithHintAsync,
            setOrToggleAcceptedRateChangeAsync,
            setAccountTypeAsync,
            getSelectionsToCompare,
            ratesChanged,
            setIsOTBSettingSet,
            isMtsTicketEmpty
        }

    });


export const prepareNewMtsTicket = (index: number, env: ICommonAppEnvWithSBBaseApi): Instance<typeof MtsTicket> => {
    return MtsTicket.create(<SnapshotOut<typeof MtsTicket>>{
        index: index,
        stake: NumberFromInput.create(<SnapshotOut<typeof NumberFromInput>>{ isValid: true, value: env.settingsFromApi.stakeMin }),
        oddsChangeType: OddsChangeType.None,
        offerType: OfferType.Prematch,
        //productId: 0,
        ticketType: TicketType.Simple,
        //barCode: '',
        potentialWin: 0,
        rate: 0,
        stakeType: StakeType.All,
        maxStake: 0,
        stakeWhole: 0,
        selectedAccountType: null,
        amlOptions: { items: [] },

        id: null,
        auxBetStorageData: null,
        auxIsSystem: false,
        availableAccountTypes: [],
        bets: null,
        errors: null,
        errorsAboveSubmitButtons: null,
        handlingFeePointsValue: null,
        handlingFeeValue: null,
        hint: null,
        hintForUser: null,
        isAcceptChangedRatesAndEvalInProgress: null,
        isAcceptingHintInProgress: null,
        isTicketCopyInProgress: null,
        isValidateTicketInProgress: null,
        oddsRateAdvantage: null,
        productFee: null,
        rateAdvantageFactor: null,
        savedTicketExpiration: null,
        savedTicketNumber: null,
        selections: [],
        stakeMoneyForInfo: null,
        ticketState: TicketStatusType.New,
        waitingInPayProgress: null,
        isOTBSettingSet: false
    }, env);
};